import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  ClipboardList,
  FileText,
  MapPin,
  Phone,
  TestTube2,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import { PageMeta } from "@/components/site/PageMeta";

const NotFound = () => {
  const { lang, t } = useLang();
  const sv = lang === "sv";


  const title = sv ? "Sidan hittades inte | Intygsläkaren" : "Page not found | Intygsläkaren";
  const desc = sv
    ? "Sidan du letar efter kunde inte hittas. Gå tillbaka till startsidan eller se alla läkarintyg."
    : "The page you are looking for could not be found. Go back to home or view all medical certificates.";

  const homeHref = sv ? "/" : "/en";
  const links = [
    {
      to: sv ? "/lakarintyg" : "/en/lakarintyg",
      label: sv ? "Alla läkarintyg" : "All medical certificates",
      text: sv ? "Hitta rätt intyg och boka tid." : "Find the right certificate and book.",
      icon: FileText,
    },
    {
      to: sv ? "/boka-tid" : "/en/book-appointment",
      label: t.nav.book,
      text: sv ? "Boka läkarbesök online." : "Book a doctor’s appointment online.",
      icon: CalendarDays,
    },
    {
      to: sv ? "/drogtestning" : "/en/drug-testing",
      label: t.nav.drugTesting,
      text: sv ? "Privat drogtestning och intyg." : "Private drug testing and certificates.",
      icon: TestTube2,
    },
    {
      to: sv ? "/provtagning" : "/en/sample-collection",
      label: sv ? "Provtagning nära dig" : "Sample collection near you",
      text: sv ? "Hitta provtagningsställen i Sverige." : "Find collection locations across Sweden.",
      icon: MapPin,
    },
    {
      to: sv ? "/foretag" : "/en/companies",
      label: t.nav.company,
      text: sv ? "Intyg och kontroller för arbetsgivare." : "Certificates and checks for employers.",
      icon: Building2,
    },
    {
      to: sv ? "/guider" : "/en/guides",
      label: sv ? "Guider" : "Guides",
      text: sv ? "Krav, förberedelser och praktisk hjälp." : "Requirements, preparation and practical help.",
      icon: ClipboardList,
    },
    {
      to: sv ? "/kontakt" : "/en/contact",
      label: t.nav.contact,
      text: sv ? "Ring eller skicka ett meddelande." : "Call or send us a message.",
      icon: Phone,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        <section className="relative overflow-hidden bg-gradient-soft pt-12 pb-16 sm:pt-16 lg:pt-24 lg:pb-24">
          <div className="absolute inset-x-0 top-0 -z-10 h-[480px] bg-[radial-gradient(ellipse_at_top,_hsl(var(--primary-soft))_0%,_transparent_60%)]" />
          <div className="container-wide">
            <div className="mx-auto max-w-3xl rounded-3xl border border-border/60 bg-card p-8 text-center shadow-elevated sm:p-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-primary">404</p>
              <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-primary sm:text-5xl">
                {sv ? "Sidan kunde inte hittas" : "Page not found"}
              </h1>
              <p className="mt-5 text-base text-muted-foreground sm:text-lg">
                {sv
                  ? "Sidan du letar efter finns inte eller har flyttats. Välj ett alternativ nedan så hjälper vi dig vidare."
                  : "The page you are looking for does not exist or has been moved. Choose an option below and we’ll help you continue."}
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button asChild variant="hero" size="xl">
                  <Link to={homeHref}>{sv ? "Tillbaka till startsidan" : "Back to home"}</Link>
                </Button>
                <Button asChild variant="ghost-outline" size="xl">
                  <Link to={sv ? "/starta-arende" : "/en/start-case"}>
                    {sv ? "Starta ärende" : "Start case"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="mx-auto mt-12 max-w-4xl">
              <h2 className="text-center font-display text-xl font-semibold text-foreground sm:text-2xl">
                {sv ? "Populära sidor" : "Popular pages"}
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {links.map(({ to, label, text, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    className="group flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-5 text-left shadow-card transition-smooth hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated sm:rounded-3xl sm:p-6"
                  >
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 font-display text-base font-semibold text-foreground">
                        {label}
                        <ArrowRight className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                      </span>
                      <span className="mt-1 block text-sm text-muted-foreground">{text}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
